import { Inject, Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { filter, first, map, shareReplay, switchMap } from 'rxjs/operators';
import {
  MainTableColumn,
  MainTableColumnId,
  MainTableSorting,
} from '@prlw/core/monitor/types/main-table';
import { MAIN_TABLE_COLUMNS_STATE } from '@prlw/core/monitor/monitor-core.module';
import { ColumnsState } from '@prlw/libs/columns-state/columns.state';
import {
  getMainTableSortDefault,
  mainTableCompareData,
} from '@prlw/core/monitor/utils/main-table';
import { UserSettingsController } from '@prlw/core/user-settings/user-settings.controller';
import { Setting } from '@prlw/core/user-settings/setting.interface';
import { Instrument } from '@prlw/core/monitor/instrument.entity';
import { MainTableInstrumentsService } from '@prlw/core/monitor/main-table-instruments.service';
import { ResponsiveService } from '@prlw/libs/responsive/responsive.service';
import { SessionAgeStatus } from '@prlw/core/monitor/session-age-status.enum';
import { BasisDetailsService } from '../../table-content/basis-details/basis-details.service';

const MAIN_TABLE_SORTING_SETTING = new Setting<MainTableSorting>(
  'main-table-sorting',
);

@Injectable({
  providedIn: 'root',
})
export class MainTableService {
  public readonly sorting$: Observable<MainTableSorting>;
  public readonly columns$: Observable<MainTableColumn<MainTableColumnId>[]>;
  public readonly visibleColumnsLocalPriceFirst$: Observable<
    MainTableColumn<MainTableColumnId>[]
  >;
  public readonly visibleColumns$: Observable<
    MainTableColumn<MainTableColumnId>[]
  >;
  public readonly sortedInstruments$: Observable<Instrument[]>;

  constructor(
    private readonly instrumentsService: MainTableInstrumentsService,
    @Inject(MAIN_TABLE_COLUMNS_STATE)
    private readonly columnsState: ColumnsState<MainTableColumnId>,
    private readonly userSettingsController: UserSettingsController,
    private readonly basisDetailsService: BasisDetailsService,
    private readonly responsiveService: ResponsiveService,
  ) {
    this.sorting$ = userSettingsController
      .setting$(
        MAIN_TABLE_SORTING_SETTING,
        getMainTableSortDefault(MainTableColumnId.BASIS),
      )
      .pipe(shareReplay(1));
    this.columns$ = columnsState.columns$;
    this.visibleColumns$ = columnsState.activeColumns$;
    this.visibleColumnsLocalPriceFirst$ = combineLatest([
      this.visibleColumns$,
      responsiveService.xsmall$,
    ]).pipe(
      map(([visibleColumns, xsmall]) =>
        xsmall
          ? [
              visibleColumns.find(
                (item) => item.id === MainTableColumnId.BASIS,
              ),
              visibleColumns.find(
                (item) => item.id === MainTableColumnId.LOCAL_PRICE,
              ),
              ...visibleColumns.filter(
                (item) =>
                  ![
                    MainTableColumnId.BASIS,
                    MainTableColumnId.LOCAL_PRICE,
                  ].includes(item.id),
              ),
            ].filter(
              (item): item is MainTableColumn<MainTableColumnId> =>
                item !== undefined,
            )
          : [...visibleColumns],
      ),
    );
    const nonNullableInstruments$ = instrumentsService.instruments$.pipe(
      filter((instruments): instruments is NonNullable<Instrument[]> =>
        Boolean(instruments),
      ),
    );
    this.sortedInstruments$ = combineLatest([
      nonNullableInstruments$.pipe(
        map((instruments) =>
          instruments.filter(
            (item) => item.quote.dealStatus !== SessionAgeStatus.OldSession,
          ),
        ),
      ),
      nonNullableInstruments$.pipe(
        map((instruments) =>
          instruments.filter(
            (item) => item.quote.dealStatus === SessionAgeStatus.OldSession,
          ),
        ),
      ),
      this.sorting$,
    ]).pipe(
      map(([instruments, oldSessionInstruments, sorting]) => [
        ...instruments.sort((a, b) => mainTableCompareData(a, b, sorting)),
        ...oldSessionInstruments.sort((a, b) =>
          mainTableCompareData(a, b, sorting),
        ),
      ]),
    );
  }

  public updateColumns(columns: MainTableColumn<MainTableColumnId>[]): void {
    this.columnsState.updateColumns$(columns);
  }

  public get gridSize$(): Observable<string> {
    return this.visibleColumnsLocalPriceFirst$.pipe(
      map((columns) =>
        columns
          .map((column, columnIndex) => {
            const min = (column.cellSizeMin ?? 0).toString();
            const columnIsFirstOrLast =
              columnIndex === 0 || columnIndex === columns.length - 1;
            const max = columnIsFirstOrLast
              ? (column.cellSizeMax ?? column.cellSizeMin ?? 0).toString()
              : '1fr';
            return `minmax(${min}, ${max})`;
          })
          .join(' '),
      ),
    );
  }

  public sortColumn(
    columnId: MainTableColumnId,
    direction?: 'ASC' | 'DES',
  ): void {
    this.sorting$
      .pipe(
        first(),
        switchMap((sorting) => {
          const { columnId: oldColumn, direction: oldDirection } = sorting;
          const defaultSort = getMainTableSortDefault(columnId);
          if (defaultSort.columnId === oldColumn) {
            defaultSort.direction = oldDirection === 'ASC' ? 'DES' : 'ASC';
            defaultSort.direction = direction ?? defaultSort.direction;
          }
          return this.userSettingsController.updateSetting(
            MAIN_TABLE_SORTING_SETTING,
            defaultSort,
          );
        }),
      )
      .subscribe();
  }

  public toggleColumnVisible(columnId: MainTableColumnId): void {
    this.columnsState.toggleColumn(columnId);
    this.sorting$.pipe(first()).subscribe((sorting) => {
      if (sorting.columnId === columnId) {
        this.sortColumn(MainTableColumnId.BASIS);
      }
    });
  }

  isExpanded$ = this.basisDetailsService.currentItem$.pipe(
    map((currentItem) => currentItem === 'lastDeals'),
  );
}
